import React, { useState, useEffect } from 'react';
import ReactMapGL, { Marker, Popup } from 'react-map-gl';
import styled, { createGlobalStyle } from 'styled-components';

import { MapboxGlobalStyles } from 'src/vendor/mapbox-global-styles';
import { MAPBOX_TOKEN } from 'shared/constants';

// ECOMM-13538: Mapbox equivalent of GoogleDeliveryMap -- a static pin + address text, matching
// what GMap actually renders today. Mirrors blaze.jsx's TruckMap (ECOMM-13535).
export default function MapboxDeliveryMap({ initialCenter, width, height, tag }) {
  const { lat, lng } = initialCenter || {};

  // Controlled viewport, paired with onViewportChange so pan/zoom gestures stick.
  const [viewport, setViewport] = useState({ latitude: lat, longitude: lng, zoom: 15 });

  useEffect(() => {
    if (lat != null && lng != null) {
      setViewport((current) => ({ ...current, latitude: lat, longitude: lng }));
    }
  }, [lat, lng]);

  if (lat == null || lng == null) {
    return null;
  }

  return (
    <MapboxContainer>
      <MapboxGlobalStyles />
      <PopupStyles />
      <div style={{ height, width }}>
        <ReactMapGL
          {...viewport}
          width='100%'
          height='100%'
          mapStyle='mapbox://styles/mapbox/streets-v11'
          mapboxApiAccessToken={MAPBOX_TOKEN}
          onViewportChange={setViewport}
        >
          <Marker latitude={lat} longitude={lng} offsetLeft={-13} offsetTop={-37}>
            <Pin />
          </Marker>
          {tag && (
            <Popup
              latitude={lat}
              longitude={lng}
              anchor='bottom'
              offsetTop={-40}
              closeButton={false}
              closeOnClick={false}
            >
              {tag}
            </Popup>
          )}
        </ReactMapGL>
      </div>
    </MapboxContainer>
  );
}

// Matches Google's default red marker (#EA4335) so the pin looks the same across both branches
// of the flag. Same teardrop shape as /icons/location-icon.svg, just recolored -- that asset is
// a fixed grey and isn't ours to recolor in place (other consumers, e.g. blaze.jsx, still use it).
function Pin() {
  return (
    <svg width={26} height={40} viewBox='0 0 13 20' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='m6.5 0a6.38 6.38 0 0 0 -6.5 6.25c0 5.97 6.5 13.75 6.5 13.75s6.5-7.78 6.5-13.75a6.38 6.38 0 0 0 -6.5-6.25zm0 9.7a3.44 3.44 0 0 1 -3.5-3.37 3.44 3.44 0 0 1 3.5-3.33 3.44 3.44 0 0 1 3.5 3.33 3.44 3.44 0 0 1 -3.5 3.37z'
        fill='#ea4335'
      />
    </svg>
  );
}

const MapboxContainer = styled.div`
  text-align: left;
`;

// react-map-gl's Popup renders plain, unstyled markup (.mapboxgl-popup-content etc.) and relies
// entirely on mapbox-gl's own CSS for its white rounded box + pointer triangle. The shared
// src/vendor/mapbox-global-styles copy fails to parse past a certain point once run through
// this app's styled-components/stylis pipeline (verified live: everything through
// .mapboxgl-ctrl-logo applies, everything from .mapboxgl-popup onward silently doesn't) --
// no prior consumer (blaze.jsx, heatmap-map.tsx) ever rendered a Popup, so this never surfaced
// before. Rather than debug/rewrite that large 800+ line vendor copy, replicate just the rules
// this component's Popup (anchor='bottom', no close button) actually needs.
const PopupStyles = createGlobalStyle`
  .mapboxgl-popup {
    display: flex;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    will-change: transform;
  }

  .mapboxgl-popup-anchor-bottom {
    flex-direction: column-reverse;
  }

  .mapboxgl-popup-tip {
    align-self: center;
    border: 10px solid transparent;
    height: 0;
    width: 0;
    z-index: 1;
  }

  .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
    border-bottom: none;
    border-top-color: #fff;
  }

  .mapboxgl-popup-content {
    background: #fff;
    border-radius: 3px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    padding: 10px 10px 15px;
    pointer-events: auto;
    position: relative;
  }
`;
